import { getSecureValue, isWeb, isPhone, isIOS, VERSION } from '@helpers';
import { getTrackingInstance, isTrackingInitialized } from './initTracking';
import type { TrackingEventMap, TrackingGlobalContext } from '@services/tracking/types';

let cachedGlobalContext: TrackingGlobalContext | null = null;

const getGlobalContext = (): TrackingGlobalContext => {
  if (cachedGlobalContext) return cachedGlobalContext;

  let buildNumber = 'web';
  if (!isWeb) {
    try {
      const DeviceInfo = require('react-native-device-info');
      buildNumber = DeviceInfo.getBuildNumber();
    } catch {
      buildNumber = 'unknown';
    }
  }

  const platform = isWeb ? 'web' : isIOS ? 'ios' : 'android';
  const deviceType = isPhone ? 'phone' : isWeb ? 'desktop' : 'tablet';

  cachedGlobalContext = {
    appVersion: VERSION,
    buildNumber,
    platform,
    deviceType,
  };

  return cachedGlobalContext;
};

// Type-safe overload for known events
export function trackEvent<E extends keyof TrackingEventMap>(
  eventName: E,
  properties: TrackingEventMap[E],
): void;
// Fallback overload for backwards compatibility during migration
export function trackEvent(eventName: string, properties?: Record<string, unknown>): void;
export function trackEvent(eventName: string, properties: Record<string, unknown> = {}): void {
  if (!isTrackingInitialized()) return;

  // Anonymous user guard — do not track events for unauthenticated users
  const userEmail = getSecureValue('email');
  if (!userEmail) return;

  // Skip E2E/automated test traffic (Playwright, Puppeteer, Selenium)
  if (isWeb && (navigator.webdriver || navigator.userAgent.includes('HeadlessChrome'))) return;

  const instance = getTrackingInstance();
  if (!instance) return;

  const globalContext = getGlobalContext();
  const sessionId = getSecureValue('appHubID') || undefined;
  const tenantId = getSecureValue('tid') || undefined;

  instance.track(eventName, {
    ...globalContext,
    'Session ID': sessionId,
    'Tenant ID': tenantId,
    ...properties,
  });
}
